import React, { useState, useEffect } from 'react';
import { signInWithCustomToken, signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../config/firebase';
import { useNavigate } from 'react-router-dom';
import { deriveTestAuthState, VALID_ROLES, type RoleType } from './testAuthState';

const ROLE_LABELS: Record<RoleType, string> = {
  admin: 'Admin — Full system access',
  nurse: 'Nurse — Patient care and support',
  content: 'Content — Content creation and management',
  developer: 'Developer — Technical development and debugging',
  analyst: 'Analyst — Analytics dashboard access',
  operations: 'Operations — Operational management',
  sales: 'Sales — Partner enquiries only',
  growth: 'Growth — Own profile only (works through MCP)',
};

const TestAuth: React.FC = () => {
  const [role, setRole] = useState<RoleType>('admin');
  const [multiMode, setMultiMode] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<RoleType[]>(['admin']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [userExists, setUserExists] = useState(false);
  const [checkingUser, setCheckingUser] = useState(false);
  const [autoLoginTriggered, setAutoLoginTriggered] = useState(false);
  const navigate = useNavigate();
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:9086';

  // The effective roles sent to the backend
  const effectiveRoles = multiMode ? selectedRoles : [role];

  // Generate dynamic email based on role and timestamp (ms + random suffix for uniqueness)
  const generateEmail = (selectedRole: string): string => {
    const now = new Date();
    const timestamp = now.toISOString()
      .replace(/[-:T.]/g, '')
      .substring(0, 17); // yyyyMMddHHmmssSSS (includes milliseconds)
    const randomSuffix = Math.random().toString(36).substring(2, 6);
    return `test-${selectedRole}-${timestamp}-${randomSuffix}@luminapain.com`;
  };

  // Derive the requested state DIRECTLY from the URL (pure, no state race).
  // The auto-login effect submits these URL-derived roles, so `?role=nurse` can
  // never be raced into an admin session (YOJO-2372).
  const urlParams = deriveTestAuthState(window.location.search);
  const initialRole = urlParams.primaryRole;
  const initialRoles = urlParams.roles;
  const initialMultiMode = urlParams.multiMode;
  const initialEmail = urlParams.emailParam || generateEmail(urlParams.emailLabel);
  const [email, setEmail] = useState(initialEmail);
  const [emailProvidedViaParam] = useState(!!urlParams.emailParam);

  // Apply initial state on mount
  useEffect(() => {
    setRole(initialRole);
    setSelectedRoles(initialRoles);
    setMultiMode(initialMultiMode);
  }, []);

  // Update email when role changes (single mode) — but only if email was not provided via URL param
  useEffect(() => {
    if (emailProvidedViaParam || multiMode) return;
    const newEmail = generateEmail(role);
    setEmail(newEmail);
    checkUserExists(newEmail);
  }, [role]);

  // Update email when selectedRoles change (multi mode)
  useEffect(() => {
    if (emailProvidedViaParam || !multiMode) return;
    const label = selectedRoles.length === 1 ? selectedRoles[0] : `multi-${selectedRoles.length}`;
    const newEmail = generateEmail(label);
    setEmail(newEmail);
    checkUserExists(newEmail);
  }, [selectedRoles]);

  // Regenerate email when switching modes
  useEffect(() => {
    if (emailProvidedViaParam) return;
    if (multiMode) {
      const label = selectedRoles.length === 1 ? selectedRoles[0] : `multi-${selectedRoles.length}`;
      const newEmail = generateEmail(label);
      setEmail(newEmail);
      checkUserExists(newEmail);
    } else {
      const newEmail = generateEmail(role);
      setEmail(newEmail);
      checkUserExists(newEmail);
    }
  }, [multiMode]);

  // Auto-login support for Playwright: /test-auth?auto=true or /test-auth?auto=true&role=nurse
  // This effect runs after role and email are set
  useEffect(() => {
    // Derive the requested roles from the URL at submit time — never from the
    // `role`/`selectedRoles` state, which may not have been applied yet on the
    // first render (YOJO-2372: that race minted admin for every requested role).
    const { autoLogin, roles } = deriveTestAuthState(window.location.search);

    // Only trigger once, after email is generated
    if (autoLogin && email && !autoLoginTriggered && !loading) {
      setAutoLoginTriggered(true);
      handleTestLogin(roles);
    }
  }, [email, autoLoginTriggered, loading]);

  // YOJO-2705 [real-credential admin sign-in for TEST_ENV !== 'local']: off-local (staging), the
  // backend /test-auth custom-token endpoints do NOT exist. A Playwright test signs in a REAL,
  // pre-provisioned staff principal by calling this hook IN THE PAGE CONTEXT with credentials from
  // the test's own env — so the password never travels in a URL, the shipped bundle, or a log.
  // The hook is absent from PROD builds (guard below) and never echoes the credential.
  useEffect(() => {
    if (import.meta.env.PROD) return;
    const w = window as unknown as { __yojoE2eSignIn?: (email: string, password: string) => Promise<void> };
    w.__yojoE2eSignIn = async (signInEmail: string, password: string) => {
      await signInWithEmailAndPassword(auth, signInEmail, password);
      // Parity with the custom-token path: mark this session as test traffic.
      sessionStorage.setItem('yojo_tenancy', 'test');
      // Force a fresh ID token so the durable roles claim is present before any guarded route loads.
      if (auth.currentUser) await auth.currentUser.getIdToken(true);
      navigate('/dashboard');
    };
    return () => {
      delete w.__yojoE2eSignIn;
    };
  }, [navigate]);

  // Check if user already exists (should never happen with timestamp-based emails)
  const checkUserExists = async (emailToCheck: string) => {
    setCheckingUser(true);
    setUserExists(false);

    try {
      const response = await fetch(`${apiUrl}/test-auth/check-user-exists?email=${encodeURIComponent(emailToCheck)}`);
      if (response.ok) {
        const data = await response.json();
        setUserExists(data.exists);
      }
    } catch (err) {
      console.error('Failed to check user existence', err);
    } finally {
      setCheckingUser(false);
    }
  };

  const handleTestLogin = async (rolesOverride?: string[]) => {
    // Auto-login passes the URL-derived roles; the manual button uses the
    // current UI selection (effectiveRoles). This is the fix for the race —
    // the submit no longer depends on state having caught up to the URL.
    const rolesToSend = rolesOverride ?? effectiveRoles;
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Get tenant ID from environment
      const tenantId = import.meta.env.VITE_TENANT_ID;

      // Get custom token from backend
      const response = await fetch(`${apiUrl}/test-auth/custom-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          role: rolesToSend.join(','),
          tenantId: tenantId || undefined
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to get custom token: ${response.status}`);
      }

      const { customToken } = await response.json();

      // Sign in with Firebase using custom token (tenant ID already set globally in firebase.ts)
      await signInWithCustomToken(auth, customToken);

      // Mark this session as test traffic so the backend can skip rate limiting, etc.
      sessionStorage.setItem('yojo_tenancy', 'test');

      setSuccess(true);

      // Redirect to dashboard after successful login
      setTimeout(() => {
        navigate('/dashboard');
      }, 1000);

    } catch (err: any) {
      setError(err.message || 'Failed to authenticate');
    } finally {
      setLoading(false);
    }
  };

  // Only show this page in development
  if (import.meta.env.PROD) {
    return <div>This page is only available in development mode</div>;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full space-y-8 p-8 bg-white rounded-lg shadow">
        <div>
          <h2 className="text-center text-3xl font-extrabold text-gray-900">
            Test Authentication
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Development only - Programmatic login for testing
          </p>
        </div>

        <div className="space-y-4">
          {userExists && (
            <div className="rounded-md bg-yellow-50 border border-yellow-200 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">User Already Exists</h3>
                  <p className="mt-1 text-xs text-yellow-700">
                    This email is already registered. This should not happen with timestamp-based emails.
                    Role selection is disabled.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Generated Email
            </label>
            <div className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-sm font-mono text-gray-700">
              {email}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Auto-generated: test-{'{role}'}-{'{timestamp}'}@luminapain.com
            </p>
          </div>

          {!multiMode ? (
            <div>
              <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                Role
              </label>
              <select
                id="role"
                value={role}
                onChange={(e) => setRole(e.target.value as any)}
                disabled={userExists || checkingUser}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="admin">Admin - Full system access and user management</option>
                <option value="nurse">Nurse - Patient care and support functions</option>
                <option value="content">Content - Content creation and management</option>
                <option value="developer">Developer - Technical development and debugging</option>
                <option value="analyst">Analyst - Analytics dashboard access only</option>
                <option value="operations">Operations - Operational management and segments</option>
                <option value="sales">Sales - Partner enquiries only</option>
                <option value="growth">Growth - Own profile only (works through MCP)</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Select role for testing. URL parameter: ?role=nurse
              </p>
              <button
                type="button"
                onClick={() => {
                  setSelectedRoles([role]);
                  setMultiMode(true);
                }}
                className="mt-2 text-xs text-indigo-600 hover:text-indigo-800 underline"
              >
                Assign multiple roles
              </button>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Roles <span className="text-indigo-600">({selectedRoles.length} selected)</span>
              </label>
              <div className="mt-1 space-y-2">
                {VALID_ROLES.map((r) => (
                  <label key={r} className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer text-sm ${selectedRoles.includes(r) ? 'bg-indigo-50 text-indigo-900' : 'text-gray-700'} ${(userExists || checkingUser) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'}`}>
                    <input
                      type="checkbox"
                      checked={selectedRoles.includes(r)}
                      disabled={userExists || checkingUser}
                      onChange={() => {
                        setSelectedRoles(prev => {
                          if (prev.includes(r)) {
                            const next = prev.filter(x => x !== r);
                            return next.length > 0 ? next : prev; // keep at least one
                          }
                          return [...prev, r];
                        });
                      }}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    {ROLE_LABELS[r]}
                  </label>
                ))}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Select one or more roles. URL: ?roles=nurse,operations,content,analyst
              </p>
              <button
                type="button"
                onClick={() => {
                  setRole(selectedRoles[0] || 'admin');
                  setMultiMode(false);
                }}
                className="mt-2 text-xs text-indigo-600 hover:text-indigo-800 underline"
              >
                Back to single role
              </button>
            </div>
          )}

          <button
            onClick={() => handleTestLogin()}
            disabled={loading || !email || effectiveRoles.length === 0 || userExists || checkingUser}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Authenticating...' : checkingUser ? 'Checking...' : 'Login with Test Token'}
          </button>

          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {success && (
            <div className="rounded-md bg-green-50 p-4">
              <p className="text-sm text-green-800">
                Authentication successful! Redirecting to dashboard...
              </p>
            </div>
          )}

          <div className="mt-6 border-t pt-4">
            <h3 className="text-sm font-medium text-gray-900 mb-2">How it works:</h3>
            <ol className="text-xs text-gray-600 space-y-1 list-decimal list-inside">
              <li>Auto-generates unique email with timestamp</li>
              <li>Checks if user already exists (should never happen)</li>
              <li>Requests custom token from backend /test-auth/custom-token</li>
              <li>Backend creates Firebase custom token with role claims</li>
              <li>Signs in with Firebase using the custom token</li>
              <li>Creates new account with selected role(s)</li>
              <li>Redirects to dashboard after successful authentication</li>
            </ol>
          </div>

          <div className="mt-4 p-3 bg-green-50 rounded-md">
            <p className="text-xs text-green-800">
              <strong>Playwright Auto-Login:</strong> Navigate to <code className="bg-green-100 px-1 rounded">/test-auth?auto=true</code> or <code className="bg-green-100 px-1 rounded">/test-auth?auto=true&role=nurse</code>
            </p>
          </div>

          <div className="mt-4 p-3 bg-blue-50 rounded-md">
            <p className="text-xs text-blue-800">
              <strong>Dynamic Emails:</strong> Each login creates a fresh test account.
              This ensures roles are assigned correctly. Timestamp-based emails guarantee uniqueness.
            </p>
          </div>

          <div className="mt-4 p-3 bg-yellow-50 rounded-md">
            <p className="text-xs text-yellow-800">
              <strong>Note:</strong> This page is only available when running locally with env=local.
              The backend TestAuthController must be running at <strong>{apiUrl}</strong>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestAuth;
